import { MessageCircle, RadarIcon, UserRound, ZapIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';
import { type ReactNode } from 'react';
import { Box, Flex, TabNav, Text, UnreadDot } from '@/components/common';
import { cn } from '@/lib/utils';

interface NavItem {
  path: string;
  label: string;
  icon: ReactNode;
  /** Solid variant for the active state; falls back to `icon` (color change only) when unset. */
  activeIcon?: ReactNode;
}

function useNavItems(): NavItem[] {
  const { t } = useTranslation('common');
  return [
    // Radar's open arcs can't be filled — active state is a color change only.
    { path: '/discover', label: t('navigation.discover'), icon: <RadarIcon size={20} /> },
    {
      path: '/today',
      label: t('navigation.today'),
      icon: <ZapIcon size={20} />,
      activeIcon: <ZapIcon size={20} fill="currentColor" />,
    },
    {
      path: '/inbox',
      label: t('navigation.inbox'),
      icon: <MessageCircle size={20} />,
      activeIcon: <MessageCircle size={20} fill="currentColor" />,
    },
    {
      path: '/account',
      label: t('navigation.account'),
      icon: <UserRound size={20} />,
      activeIcon: <UserRound size={20} fill="currentColor" />,
    },
  ];
}

interface NavBarProps {
  className?: string;
  hasUnread?: boolean;
}

interface NavLabelProps {
  label: string;
  icon: ReactNode;
  activeIcon?: ReactNode;
  active?: boolean;
  trailing?: ReactNode;
}

function NavLabel({ label, icon, activeIcon, active = false, trailing }: NavLabelProps) {
  return (
    <Flex direction="column" align="center" gap="1" py="1">
      <Flex align="center" className={cn('relative', active && 'text-accent-9')} aria-hidden>
        {active && activeIcon ? activeIcon : icon}
        <Box position="absolute" right="-0.75em" top="0">
          {trailing}
        </Box>
      </Flex>
      <Text size="0.5" color="gray" weight="bold">
        {label}
      </Text>
    </Flex>
  );
}

const NavBar = ({ className, hasUnread = false }: NavBarProps) => {
  const { t } = useTranslation('common');
  const { pathname } = useLocation();
  const navItems = useNavItems();

  return (
    <nav className={cn('shrink-0', className)}>
      <TabNav.Root variant="nav">
        {navItems.map(({ path, label, icon, activeIcon }) => {
          const isActive = pathname === path || pathname.startsWith(`${path}/`);
          const showUnreadDot = hasUnread && path === '/inbox' && !isActive;

          return (
            <TabNav.Link key={path} asChild active={isActive}>
              <Link
                to={path}
                data-testid={`nav-${path.slice(1)}`}
                aria-label={showUnreadDot ? t('navigation.unreadLabel', { label }) : undefined}
              >
                <NavLabel
                  label={label}
                  icon={icon}
                  activeIcon={activeIcon}
                  active={isActive}
                  trailing={showUnreadDot && <UnreadDot size="3" />}
                />
              </Link>
            </TabNav.Link>
          );
        })}
      </TabNav.Root>
    </nav>
  );
};

export { NavBar };
export type { NavBarProps };
